/**
 * Competitor share of voice, computed from a report's executions.
 *
 * One pure function feeds every surface that shows a report's competitor
 * table: the finalize step that stores `AiReport.competitorAnalysis`, the
 * report export (PDF/PPT) and the web report's competitor-analysis endpoint.
 * The export and the endpoint recompute at read time with the current matcher
 * rather than trusting the stored JSON, which was written once with whatever
 * matcher existed then.
 *
 * The logo grid ("who gets mentioned where") is built from the same
 * `resolvePlatformExecutionMentions` over the same prompt set
 * (`isSovDiscoveryExecution`), so a competitor with a logo in the grid always
 * has a non-zero share in the table.
 *
 * Pure — no db, no server-only imports. Exported from `@repo/core/client`.
 */

import { isExcludedFromDiscoveryMetrics } from "../types/discovery-query";
import {
	type BrandMentionFocal,
	type BrandMentionMatcher,
	buildBrandMentionMatcher,
	resolveMention,
} from "./brand-mention-match";

export interface CompetitorSOVMetrics {
	competitorId: string;
	competitorName: string;
	competitorDomain: string;

	// Overall metrics
	shareOfVoice: number; // Percentage of platform executions where competitor appears
	totalMentions: number; // Total times competitor appears across all platforms
	totalCitations: number; // Total citations to competitor domain

	// Per-query breakdown
	queriesAppeared: number; // Number of discovery queries where competitor appears
	queriesWithBrandAndCompetitor: number; // Both appear
	queriesCompetitorOnly: number; // Competitor appears, brand doesn't (gaps)

	// Detailed gap analysis
	gapQueries: Array<{
		queryId: string;
		query: string;
		queryType: string;
		competitorMentions: number;
		competitorCitations: number;
		platformsWithCompetitor: string[]; // Which platforms mentioned the competitor
	}>;

	// Platform-level metrics
	platformMentions: {
		[platform: string]: number; // Count of mentions per platform
	};
	platformAppearances?: number; // Count of platform executions where competitor appears (for SOV calculation)
}

export interface CompetitorAnalysisResult {
	reportId: string;
	brandDomain: string;
	brandMentions: number;
	brandCitations: number;
	brandShareOfVoice: number; // Percentage: (brandPlatformAppearances / totalPlatformsAnalyzed) * 100
	brandPlatformAppearances?: number; // Number of platform executions where brand appeared

	totalQueriesAnalyzed: number;
	totalPlatformsAnalyzed: number; // Total platform executions (queries × platforms that completed)

	competitors: CompetitorSOVMetrics[];

	// Summary stats
	totalGapQueries: number; // Queries where at least one competitor appears but brand doesn't
	competitorsOutperformingBrand: number; // Count of competitors with higher SOV than brand
}

export interface SovBrandMentionLike {
	brandName: string;
	inSummary?: boolean | null;
}

export interface SovCitationLike {
	url?: string | null;
	isOurDomain?: boolean | null;
}

export interface SovPlatformExecutionLike {
	platform: string;
	error?: string | null;
	completedAt?: Date | string | null;
	brandMentions?: ReadonlyArray<SovBrandMentionLike> | null;
	citations?: ReadonlyArray<SovCitationLike> | null;
}

export interface SovDiscoveryExecutionLike {
	id: string;
	propertyQuery?: { query?: string | null; queryType?: string | null } | null;
	platformExecutions: ReadonlyArray<SovPlatformExecutionLike>;
}

export interface SovCompetitorLike {
	id: string;
	url?: string | null;
	name?: string | null;
	aliases?: ReadonlyArray<string | null | undefined> | null;
}

/** Domain part of a URL/host, lowercased, without scheme / www / path. */
export function normalizeSovDomain(domain: string | null | undefined): string {
	if (!domain) return "";
	return domain
		.toLowerCase()
		.replace(/^https?:\/\//, "")
		.replace(/^www\./, "")
		.replace(/\/$/, "")
		.split("/")[0];
}

/**
 * Whether a prompt counts toward share of voice. Brand and comparison prompts
 * name the brand (and, for comparisons, the competitors) in the question, so
 * they are excluded — the same rule the DiscoveryMetric writer applies.
 */
export function isSovDiscoveryExecution(execution: {
	propertyQuery?: { queryType?: string | null } | null;
}): boolean {
	return !isExcludedFromDiscoveryMetrics(
		execution.propertyQuery?.queryType ?? "",
	);
}

/** A platform execution that produced an answer (no error, completed). */
export function isCompletedPlatformExecution(pe: {
	error?: string | null;
	completedAt?: Date | string | null;
}): boolean {
	return !pe.error && !!pe.completedAt;
}

/** Who one answer named, after every mention is resolved once. */
export interface ResolvedPlatformMentions {
	/** Mention rows that resolved to our brand. */
	selfMentions: number;
	/** Mention rows per competitor id (only competitors with ≥1 mention). */
	competitorMentions: Map<string, number>;
}

/**
 * Resolve every in-summary mention of one answer. Only `inSummary === true`
 * rows count: Google AI Overview stores its organic results as mention rows
 * with `inSummary: false`, and those are not AI mentions.
 */
export function resolvePlatformExecutionMentions(
	pe: Pick<SovPlatformExecutionLike, "brandMentions">,
	matcher: BrandMentionMatcher,
): ResolvedPlatformMentions {
	let selfMentions = 0;
	const competitorMentions = new Map<string, number>();
	for (const mention of pe.brandMentions ?? []) {
		if (mention?.inSummary !== true) continue;
		const resolved = resolveMention(mention.brandName, matcher);
		if (!resolved) continue;
		if (resolved.kind === "self") {
			selfMentions++;
		} else {
			competitorMentions.set(
				resolved.id,
				(competitorMentions.get(resolved.id) ?? 0) + 1,
			);
		}
	}
	return { selfMentions, competitorMentions };
}

/**
 * Build the matcher the SOV table and the logo grid share. `focal: null` is a
 * subject with no focal brand (CATEGORY).
 */
export function buildSovMentionMatcher(input: {
	focal: BrandMentionFocal | null | undefined;
	competitors: ReadonlyArray<SovCompetitorLike>;
}): BrandMentionMatcher {
	return buildBrandMentionMatcher({
		focal: input.focal,
		competitors: input.competitors.map((c) => ({
			id: c.id,
			name: c.name,
			url: c.url,
			aliases: c.aliases,
		})),
	});
}

/**
 * Share of voice and gap analysis for every competitor, at the platform
 * execution level: SOV = answers naming the entity / completed answers.
 */
export function computeCompetitorSOV(input: {
	reportId: string;
	brandDomain: string;
	discoveryExecutions: ReadonlyArray<SovDiscoveryExecutionLike>;
	competitors: ReadonlyArray<SovCompetitorLike>;
	/** Our brand's names; `null` for a subject with no focal brand. */
	focal: BrandMentionFocal | null;
}): CompetitorAnalysisResult {
	const matcher = buildSovMentionMatcher({
		focal: input.focal,
		competitors: input.competitors,
	});

	const competitorMetrics = new Map<string, CompetitorSOVMetrics>();
	for (const competitor of input.competitors) {
		const competitorDomain = normalizeSovDomain(competitor.url);
		competitorMetrics.set(competitor.id, {
			competitorId: competitor.id,
			competitorName: competitor.name || competitorDomain,
			competitorDomain,
			shareOfVoice: 0,
			totalMentions: 0,
			totalCitations: 0,
			queriesAppeared: 0,
			queriesWithBrandAndCompetitor: 0,
			queriesCompetitorOnly: 0,
			gapQueries: [],
			platformMentions: {},
			platformAppearances: 0,
		});
	}

	let brandMentions = 0;
	let brandCitations = 0;
	let brandPlatformAppearances = 0;
	let totalPlatformsAnalyzed = 0;

	const sovExecutions = input.discoveryExecutions.filter(
		isSovDiscoveryExecution,
	);
	const gapQueryIds = new Set<string>();

	for (const discoveryExecution of sovExecutions) {
		const completedPlatforms = discoveryExecution.platformExecutions.filter(
			isCompletedPlatformExecution,
		);
		if (completedPlatforms.length === 0) continue;
		totalPlatformsAnalyzed += completedPlatforms.length;

		let brandAppearsInQuery = false;
		const perCompetitor = new Map<
			string,
			{ mentions: number; citations: number; platforms: string[] }
		>();

		for (const pe of completedPlatforms) {
			const resolved = resolvePlatformExecutionMentions(pe, matcher);

			if (resolved.selfMentions > 0) {
				brandAppearsInQuery = true;
				brandPlatformAppearances++;
				brandMentions += resolved.selfMentions;
			}
			// Citations alone never make an entity "appear"; they are tracked
			// as a separate signal. With no focal brand nothing is "ours".
			if (input.focal) {
				brandCitations += (pe.citations ?? []).filter(
					(c) => c.isOurDomain === true,
				).length;
			}

			for (const [competitorId, metrics] of competitorMetrics) {
				const mentions =
					resolved.competitorMentions.get(competitorId) ?? 0;
				const citations = metrics.competitorDomain
					? (pe.citations ?? []).filter((c) => {
							if (!c.url) return false;
							try {
								return (
									normalizeSovDomain(
										new URL(c.url).hostname,
									) === metrics.competitorDomain
								);
							} catch {
								return false;
							}
						}).length
					: 0;

				const acc = perCompetitor.get(competitorId) ?? {
					mentions: 0,
					citations: 0,
					platforms: [],
				};
				acc.citations += citations;
				if (mentions > 0) {
					acc.mentions += mentions;
					acc.platforms.push(pe.platform);
					metrics.platformAppearances =
						(metrics.platformAppearances ?? 0) + 1;
					metrics.platformMentions[pe.platform] =
						(metrics.platformMentions[pe.platform] ?? 0) + mentions;
				}
				perCompetitor.set(competitorId, acc);
			}
		}

		for (const [competitorId, metrics] of competitorMetrics) {
			const acc = perCompetitor.get(competitorId);
			if (!acc || acc.platforms.length === 0) continue;

			metrics.queriesAppeared++;
			metrics.totalMentions += acc.mentions;
			metrics.totalCitations += acc.citations;

			if (!brandAppearsInQuery) {
				metrics.queriesCompetitorOnly++;
				gapQueryIds.add(discoveryExecution.id);
				metrics.gapQueries.push({
					queryId: discoveryExecution.id,
					query: discoveryExecution.propertyQuery?.query ?? "",
					queryType:
						discoveryExecution.propertyQuery?.queryType ?? "",
					competitorMentions: acc.mentions,
					competitorCitations: acc.citations,
					platformsWithCompetitor: acc.platforms,
				});
			} else {
				metrics.queriesWithBrandAndCompetitor++;
			}
		}
	}

	const brandShareOfVoice =
		totalPlatformsAnalyzed > 0
			? (brandPlatformAppearances / totalPlatformsAnalyzed) * 100
			: 0;
	for (const metrics of competitorMetrics.values()) {
		metrics.shareOfVoice =
			totalPlatformsAnalyzed > 0
				? ((metrics.platformAppearances ?? 0) /
						totalPlatformsAnalyzed) *
					100
				: 0;
	}

	const competitorsOutperformingBrand = Array.from(
		competitorMetrics.values(),
	).filter((m) => m.shareOfVoice > brandShareOfVoice).length;

	return {
		reportId: input.reportId,
		brandDomain: input.brandDomain,
		brandMentions,
		brandCitations,
		brandShareOfVoice: Math.round(brandShareOfVoice * 10) / 10,
		brandPlatformAppearances,
		totalQueriesAnalyzed: sovExecutions.length,
		totalPlatformsAnalyzed,
		competitors: Array.from(competitorMetrics.values()).map((m) => ({
			...m,
			shareOfVoice: Math.round(m.shareOfVoice * 10) / 10,
		})),
		totalGapQueries: gapQueryIds.size,
		competitorsOutperformingBrand,
	};
}

/** One cell of the "who gets mentioned where" grid. */
export interface SovGridCell {
	platform: string;
	/** Our brand was named in this answer. */
	self: boolean;
	/** Competitor ids named in this answer, in matcher (property) order. */
	competitorIds: string[];
}

/** One prompt row of the grid. */
export interface SovGridRow {
	executionId: string;
	query: string;
	queryType: string;
	cells: SovGridCell[];
}

/**
 * The logo grid, from the same prompt set and the same mention resolution as
 * `computeCompetitorSOV`. `platformKeys` fixes the column order; a platform
 * with no completed answer for a prompt yields an empty cell.
 */
export function buildSovMentionGrid(input: {
	discoveryExecutions: ReadonlyArray<SovDiscoveryExecutionLike>;
	matcher: BrandMentionMatcher;
	platformKeys: ReadonlyArray<string>;
}): SovGridRow[] {
	const order = new Map(
		input.matcher.competitors.map((c, index) => [c.id, index]),
	);
	return input.discoveryExecutions
		.filter(isSovDiscoveryExecution)
		.map((exec) => ({
			executionId: exec.id,
			query: exec.propertyQuery?.query ?? "",
			queryType: exec.propertyQuery?.queryType ?? "",
			cells: input.platformKeys.map((platform) => {
				const pe = exec.platformExecutions.find(
					(p) => p.platform === platform,
				);
				if (!pe || !isCompletedPlatformExecution(pe)) {
					return { platform, self: false, competitorIds: [] };
				}
				const resolved = resolvePlatformExecutionMentions(
					pe,
					input.matcher,
				);
				return {
					platform,
					self: resolved.selfMentions > 0,
					competitorIds: Array.from(
						resolved.competitorMentions.keys(),
					).sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)),
				};
			}),
		}));
}
